import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import axios from "axios";

import {
  Stack,
  Grid,
  Typography,
  Button,
  Avatar,
  Chip,
  TextField,
  IconButton,
  Popover,
  Badge,
  Autocomplete,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { IUserResponse } from "../types/user";
import PermissionGuard from "../components/PermissionGuard";
import PERMISSIONS from "../constants/permissions";
import { ClinicResponse } from "../types/clinic";
import ROLES, { ROLES_LIST } from "../constants/roles";
import { LoadingButton } from "@mui/lab";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleFullScreenButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
} from "material-react-table";

const Users: React.FC = () => {
  const navigate = useNavigate();

  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<IUserResponse[]>([]);

  const [filterValues, setFilterValues] = useState<IFilterValues>({});
  const [filtersAnchorEl, setFiltersAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState<string | undefined>();

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFiltersAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFiltersAnchorEl(null);
  };

  const handleFilterChange = useCallback((values: IFilterValues) => {
    setFilterValues(values);
  }, []);

  const handleSync = useCallback(() => {
    setIsSyncing(true);
    axios
      .post("/users/sync")
      .then((res) => {
        console.log("res:", res);
      })
      .catch((err) => {
        console.log("error:", err);
      })
      .finally(() => {
        setIsSyncing(false);
      });
  }, []);

  const loadData = useCallback(() => {
    setIsLoading(true);
    axios
      .get<IUserResponse[]>("/users", {
        params: { search: search, filters: filterValues },
      })
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => {
        console.log("error:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [search, filterValues]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = useMemo<MRT_ColumnDef<IUserResponse>[]>(
    () => [
      {
        accessorKey: "image",
        header: "",
        accessorFn: (params) => {
          if (!params.image) {
            return null;
          }
          return <Avatar alt={params.name} src={params.image} />;
        },
        maxSize: 60,
        minSize: 60,
      },
      {
        accessorKey: "familyName",
        header: "Uzvārds",
        accessorFn: (params) => {
          return params.familyName;
        },
        minSize: 70,
        maxSize: 120,
        enableSorting: true,
      },
      {
        accessorKey: "givenName",
        header: "Vārds",
        accessorFn: (params) => {
          return params.givenName;
        },
        minSize: 70,
        maxSize: 120,
        enableSorting: true,
      },
      {
        accessorKey: "email",
        header: "E-pasts",
        accessorFn: (params) => {
          return params.email;
        },
      },
      {
        accessorKey: "roles",
        header: "Loma",
        accessorFn: (params) => {
          return (
            <Grid container spacing={0.5}>
              {params.roles?.map((role: ROLES) => {
                return (
                  <Grid item key={role}>
                    <Chip
                      label={ROLES_LIST[role].name}
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: "0.7rem", height: 1 }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          );
        },
      },
      {
        accessorKey: "clinics",
        header: "Klīnikas",
        accessorFn: (params) => {
          return (
            <Grid container spacing={0.5}>
              {params.clinics?.map((clinic: ClinicResponse) => {
                return (
                  <Grid key={clinic._id} item>
                    <Chip
                      label={clinic.name}
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: "0.7rem", height: 1 }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          );
        },
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4">Lietotāji</Typography>
        </Grid>
        <PermissionGuard permission={PERMISSIONS.SYNC_USERS}>
          {({ hasAccess }) => (
            <>
              {hasAccess ? (
                <Grid item>
                  <LoadingButton
                    loading={isSyncing}
                    startIcon={<AddIcon />}
                    onClick={() => handleSync()}
                  >
                    Sinhronizēt
                  </LoadingButton>
                </Grid>
              ) : null}
            </>
          )}
        </PermissionGuard>
      </Grid>
      <Grid container justifyContent="flex-end" alignItems="center" gap={1}>
        <Grid item></Grid>
      </Grid>

      <MaterialReactTable
        columns={columns}
        enableColumnActions={false}
        data={users || []}
        getRowId={(row) => row._id}
        enableTableFooter={false}
        enableGlobalFilter={false}
        enableBottomToolbar={false}
        initialState={{
          sorting: [
            {
              id: "familyName",
              desc: false,
            },
          ],
        }}
        state={{
          isLoading,
          pagination: {
            pageIndex: 0,
            pageSize: users.length || 10,
          },
        }}
        muiTableBodyRowProps={(props) => ({
          onClick: () => {
            navigate(`/users/${props.row.original._id}`);
          },
          style: { cursor: "pointer" },
        })}
        renderToolbarInternalActions={({ table }) => (
          <>
            <TextField
              label="Meklēt"
              value={search ? search : ""}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{
                maxLength: 50,
              }}
              InputProps={{
                endAdornment: search ? (
                  <IconButton size="small" onClick={() => setSearch(undefined)}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : undefined,
              }}
              size="small"
            />
            <IconButton onClick={handleFilterClick}>
              <Badge
                color="secondary"
                badgeContent={
                  Object.values(filterValues).filter((item) => item).length
                }
              >
                <FilterAltIcon />
              </Badge>
            </IconButton>

            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />
            <MRT_ToggleFullScreenButton table={table} />
          </>
        )}
      />
      <Popover
        open={Boolean(filtersAnchorEl)}
        anchorEl={filtersAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
      >
        <Filters values={filterValues} onFilterChange={handleFilterChange} />
      </Popover>
    </Stack>
  );
};

interface IFilterValues {
  search?: string;
  role?: ROLES;
  clinic?: string;
}

interface IFIlterProps {
  values: IFilterValues;
  onFilterChange: (values: IFilterValues) => void;
}

const Filters: React.FunctionComponent<IFIlterProps> = ({
  values,
  onFilterChange,
}) => {
  const [clinics, setClinics] = useState<ClinicResponse[]>([]);

  useEffect(() => {
    onFilterChange(values);
  }, [values]);

  useEffect(() => {
    axios
      .get<ClinicResponse[]>("/clinics")
      .then((res) => {
        setClinics(res.data);
      })
      .catch((err) => {
        console.log("err:", err);
      });
  }, []);

  return (
    <Stack spacing={2} sx={{ padding: 2, width: 400, maxWidth: "100%" }}>
      <Typography variant="h6">Filtri</Typography>
      <Autocomplete
        options={clinics}
        value={
          values.clinic
            ? clinics.find((clinic) => values.clinic === clinic._id)
            : null
        }
        getOptionLabel={(option) => option.name}
        renderInput={(params) => <TextField {...params} label="Klīnika" />}
        onChange={(e, value) => {
          onFilterChange({
            ...values,
            clinic: value ? value._id : undefined,
          });
        }}
      />
      <Autocomplete
        options={Object.keys(ROLES_LIST) as ROLES[]}
        value={values.role ? values.role : null}
        getOptionLabel={(option) => ROLES_LIST[option].name}
        renderInput={(params) => <TextField {...params} label="Loma" />}
        onChange={(e, value) => {
          onFilterChange({
            ...values,
            role: value ? value : undefined,
          });
        }}
      />
    </Stack>
  );
};

export default Users;
